import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Newspaper, ArrowRight } from "lucide-react";
import { getBlogBySlug, retailerLabel } from "../services/BlogService";
import type { BlogPost, BlogDeal } from "../services/BlogService";
import { getProductImageUrl } from "../utils/imageUtils";
import useSeo from "../hooks/useSeo";
import { blogPostJsonLd } from "../seo/blog";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

function BlogPostSEO({ post }: { post: BlogPost }) {
  const path = `/blog/${post.slug}`;
  const image = post.coverImage ? getProductImageUrl(post.coverImage) : undefined;
  useSeo({
    title: `${post.title} | DealHawk Blog`,
    description: post.excerpt,
    path,
    image,
    type: "article",
    jsonLd: [blogPostJsonLd(post, image)],
  });
  return null;
}

function DealRow({ deal, retailer }: { deal: BlogDeal; retailer: string }) {
  const storeName = deal.storeId ? `${retailerLabel(retailer)} #${deal.storeId}` : null;
  const dealLink = deal.dealId ? `/deals/${deal.dealId}` : null;

  const content = (
    <>
      {deal.image && (
        <img
          src={getProductImageUrl(deal.image)}
          alt=""
          className="w-10 h-10 rounded-lg object-contain bg-gray-50 dark:bg-dark-800 border border-gray-100 dark:border-dark-700 p-1 flex-shrink-0"
          loading="lazy"
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-gray-900 dark:text-white leading-snug truncate group-hover/deal:text-primary-600 dark:group-hover/deal:text-primary-400 transition-colors">
          {deal.name}
        </p>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className="text-[13px] font-bold text-emerald-600">{fmt(deal.price)}</span>
          <span className="text-[11px] text-gray-400 dark:text-dark-500 line-through">{fmt(deal.msrp)}</span>
          {deal.discount > 0 && (
            <span className="text-[11px] font-semibold text-red-500">{Math.round(deal.discount)}% off</span>
          )}
        </div>
        {storeName && (
          <p className="text-[11px] text-gray-400 dark:text-dark-500 mt-0.5">Found at {storeName}</p>
        )}
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-gray-300 dark:text-dark-600 flex-shrink-0 group-hover/deal:text-primary-500 transition-colors" />
    </>
  );

  if (dealLink) {
    return (
      <Link to={dealLink} className="flex items-center gap-3 py-3 border-b border-gray-100 dark:border-dark-800 last:border-b-0 group/deal">
        {content}
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-100 dark:border-dark-800 last:border-b-0 group/deal">
      {content}
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading, error } = useQuery<BlogPost>({
    queryKey: ["blog", slug],
    queryFn: () => getBlogBySlug(slug!),
    enabled: !!slug,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 dark:border-dark-700 border-t-primary-600" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-xs">
          <Newspaper className="w-10 h-10 text-gray-300 dark:text-dark-600 mx-auto mb-3" />
          <h1 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Post not found</h1>
          <p className="text-sm text-gray-500 dark:text-dark-400 mb-5">This post may have been removed.</p>
          <Link to="/blog" className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
            Back to blog
          </Link>
        </div>
      </div>
    );
  }

  const date = new Date(post.publishedAt);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      <BlogPostSEO post={post} />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1 text-[13px] text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 transition-colors mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Blog
        </Link>

        {/* Article header */}
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[12px] text-gray-400 dark:text-dark-500 mb-2">
            {post.retailer && (
              <>
                <span className="font-medium text-primary-600 dark:text-primary-400">{retailerLabel(post.retailer)}</span>
                <span>&middot;</span>
              </>
            )}
            <time dateTime={new Date(post.publishedAt).toISOString()}>
              {date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </time>
            {post.deals?.length > 0 && (
              <>
                <span>&middot;</span>
                <span>{post.deals.length} deals</span>
              </>
            )}
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-snug tracking-tight">
            {post.title}
          </h1>
        </header>

        {/* Deals table */}
        {post.deals?.length > 0 && (
          <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-100 dark:border-dark-800 px-4 mb-8">
            {post.deals.map((deal, i) => (
              <DealRow key={`${deal.upc}-${i}`} deal={deal} retailer={post.retailer} />
            ))}
          </div>
        )}

        {/* Article body */}
        <div
          className="prose prose-sm prose-gray dark:prose-invert max-w-none
            prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-gray-900 dark:prose-headings:text-white
            prose-h2:text-base prose-h2:mt-6 prose-h2:mb-2
            prose-p:text-[14px] prose-p:leading-[1.7] prose-p:text-gray-600 dark:prose-p:text-dark-300
            prose-a:text-primary-600 dark:prose-a:text-primary-400 prose-a:no-underline hover:prose-a:underline
            prose-img:rounded-lg prose-img:my-4
            prose-li:text-[14px] prose-li:text-gray-600 dark:prose-li:text-dark-300"
          dangerouslySetInnerHTML={{ __html: post.body }}
        />

        {/* CTA */}
        <div className="mt-10 py-6 border-t border-gray-200 dark:border-dark-800">
          <p className="text-[13px] text-gray-500 dark:text-dark-400 mb-3">
            These deals were found at specific stores. Sign up to see if they are on sale near you.
          </p>
          <Link
            to="/get-started"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            Check stores near you <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {post.tags.map((tag) => (
              <span key={tag} className="text-[11px] text-gray-400 dark:text-dark-500">#{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
